import React, { useState, useEffect } from "react";

import { useParams, Link as RouterLink } from "react-router-dom";

import { get } from "lodash";

import {
  Grid,
  Fab,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Link,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import { Refresh as RefreshIcon, Home as HomeIcon } from "@material-ui/icons";

import { firestore } from "../../firebase";

import EmptyState from "../EmptyState";
import Loader from "../Loader";

import { ReactComponent as ErrorIllustration } from "../../assets/illustrations/error.svg";
import { ReactComponent as NoDataIllustration } from "../../assets/illustrations/no-data.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  seasonHeader: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  entryCard: {
    marginBottom: theme.spacing(2),
  },
  championIcon: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1),
  },
  placeChip: {
    display: "inline-block",
    fontWeight: 600,
  },
}));

function ordinal(n) {
  if (n === undefined || n === null) return "—";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getChampionPick(entry, season) {
  if (!season || !entry) return null;

  const { rounds, teams } = season;
  if (!rounds || !teams) return null;

  const championshipGameId = get(rounds, [2, "all", 0]);
  if (!championshipGameId) return null;

  const entryPicksByGame = entry.picksByGame;
  if (!entryPicksByGame) return null;

  const championshipPick = entryPicksByGame[championshipGameId];
  if (!championshipPick) return null;

  const winnerTeamId = championshipPick.winner;
  return teams[winnerTeamId] || null;
}

function getRank(entry, season) {
  if (!season || !entry) return null;

  const { standings, currentRound } = season;
  if (!standings || !currentRound) return null;

  const roundStandings = get(standings, ["byRound", currentRound]);
  if (!roundStandings) return null;

  const standing = roundStandings.find((s) => s.id === entry.id);
  return standing ? standing.rank : null;
}

function EntryCard({ entry, season, classes }) {
  const champion = getChampionPick(entry, season);
  const rank = getRank(entry, season);

  const overallPoints = get(entry, ["scoring", "overall"]) ?? null;
  const bracketName = entry.bracketName || entry.name || "Bracket";
  const ownerName = entry.ownerName;

  return (
    <Card className={classes.entryCard} variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1">
              <strong>
                <Link component={RouterLink} to={`/seasons/${entry.seasonId}/user-entries/${entry.id}`}>
                  {bracketName}
                </Link>
              </strong>
              {ownerName && ` — ${ownerName}`}
            </Typography>
          </Box>
          {rank && (
            <Typography variant="h6" className={classes.placeChip} color="textSecondary">
              {ordinal(rank)} place
            </Typography>
          )}
        </Box>

        <Box mt={1} display="flex" alignItems="center">
          <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
            Champion pick:
          </Typography>
          {champion ? (
            <Box display="flex" alignItems="center">
              {champion.icon_path && (
                <img
                  className={classes.championIcon}
                  src={`/assets/icons/${champion.icon_path}`}
                  alt={champion.name}
                />
              )}
              <Typography variant="body2">
                {champion.seed && (
                  <Typography variant="caption" color="textSecondary" component="span" style={{ marginRight: 4 }}>
                    ({champion.seed})
                  </Typography>
                )}
                {champion.isEliminated ? <s>{champion.name}</s> : champion.name}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">None</Typography>
          )}
        </Box>

        {overallPoints !== null && (
          <Box mt={0.5}>
            <Typography variant="body2" color="textSecondary">
              Points: <strong>{overallPoints}</strong>
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function UserPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [userEntriesBySeason, setUserEntriesBySeason] = useState({});
  const [seasonData, setSeasonData] = useState({});
  const [entriesLoading, setEntriesLoading] = useState(false);
  const { userId } = useParams();
  const classes = useStyles();

  useEffect(() => {
    return firestore
      .collection("users")
      .doc(userId)
      .onSnapshot(
        (snapshot) => {
          setLoading(false);
          setUser(snapshot.data());
        },
        (err) => {
          setLoading(false);
          setError(err);
        }
      );
  }, [userId]);

  useEffect(() => {
    if (!user || !user.entriesBySeason) {
      setUserEntriesBySeason({});
      setSeasonData({});
      return;
    }

    setEntriesLoading(true);

    const seasonIds = Object.keys(user.entriesBySeason);

    Promise.all([
      // Fetch all user entries
      Promise.all(
        seasonIds.map(async (seasonId) => {
          const entryIds = user.entriesBySeason[seasonId];
          const entries = await Promise.all(
            entryIds.map((entryId) =>
              firestore
                .collection("seasons")
                .doc(seasonId)
                .collection("user_entries")
                .doc(entryId)
                .get()
                .then((doc) => ({ id: doc.id, seasonId, ...doc.data() }))
            )
          );
          return { seasonId, entries };
        })
      ),
      // Fetch all season documents
      Promise.all(
        seasonIds.map((seasonId) =>
          firestore
            .collection("seasons")
            .doc(seasonId)
            .get()
            .then((doc) => ({ seasonId, season: { id: doc.id, ...doc.data() } }))
        )
      ),
    ])
      .then(([entryResults, seasonResults]) => {
        const entriesBySeason = {};
        entryResults.forEach(({ seasonId, entries }) => {
          entriesBySeason[seasonId] = entries;
        });

        const seasons = {};
        seasonResults.forEach(({ seasonId, season }) => {
          seasons[seasonId] = season;
        });

        setUserEntriesBySeason(entriesBySeason);
        setSeasonData(seasons);
        setEntriesLoading(false);
      })
      .catch(() => {
        setEntriesLoading(false);
      });
  }, [user]);

  if (error) {
    return (
      <EmptyState
        image={<ErrorIllustration />}
        title="Couldn't retrieve user."
        description="Something went wrong when trying to retrieve the requested user."
        button={
          <Fab
            variant="extended"
            color="primary"
            onClick={() => window.location.reload()}
          >
            <Box clone mr={1}>
              <RefreshIcon />
            </Box>
            Retry
          </Fab>
        }
      />
    );
  }

  if (loading || entriesLoading) {
    return <Loader />;
  }

  if (!user) {
    return (
      <EmptyState
        image={<NoDataIllustration />}
        title="User doesn't exist."
        description="The requested user doesn't exist."
        button={
          <Fab variant="extended" color="primary" component={RouterLink} to="/">
            <Box clone mr={1}>
              <HomeIcon />
            </Box>
            Home
          </Fab>
        }
      />
    );
  }

  const seasonIds = Object.keys(userEntriesBySeason).sort((a, b) => b.localeCompare(a));
  const displayName =
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    userId;

  return (
    <Box className={classes.root}>
      <Typography variant="h5"><strong>{displayName}</strong></Typography>

      {seasonIds.length === 0 && (
        <Box mt={3}>
          <Typography variant="body1" color="textSecondary">No entries found.</Typography>
        </Box>
      )}

      {seasonIds.map((seasonId) => {
        const entries = userEntriesBySeason[seasonId] || [];
        const season = seasonData[seasonId];

        return (
          <Box key={seasonId}>
            <Typography variant="h6" className={classes.seasonHeader}>
              {seasonId}
            </Typography>
            <Divider />
            <Box mt={1}>
              {entries.map((entry) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  season={season}
                  classes={classes}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default UserPage;
